import React, { useState, useEffect } from 'react';

import type { EditorActions } from '@atlaskit/editor-core';

import { HelpManagerLoader } from '@confluence/editor-help';
import { OnboardingEditorBanner } from '@confluence/onboarding-editor';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';

import type { FullPageEditorComponentProps } from './FullPageEditorComponent';

type EditorProps = Pick<FullPageEditorComponentProps, 'contentId' | 'version' | 'spaceKey'>;

type AdditionalProps = {
	isContentCreationPanelOpen: boolean;
	isShowingOfflineBanner: boolean;
	editorActions: EditorActions;
	onClickOpenContentCreationPanel: () => void;
	handleTitleChange: (newTitle: string) => void;
	isInCompanyHubUnpublishedState?: boolean;
	editorApi: FullPageEditorPresetAPI | undefined | null;
};

type OnboardingHelpContainerProps = EditorProps & AdditionalProps;

export const OnboardingHelpContainer = ({
	contentId,
	version,
	spaceKey,
	editorApi,
	isContentCreationPanelOpen,
	isShowingOfflineBanner,
	editorActions,
	onClickOpenContentCreationPanel,
	handleTitleChange,
}: OnboardingHelpContainerProps) => {
	/**
	 * Editor Help Center conflicts with the OnboardingEditorBanner and should be suppressed when the banner is shown
	 * The banner has queries that need to fully resolve before the banner knows if it should render itself
	 * To prevent the help center from flashing on the screen, we hide it until the banner queries and logic are complete
	 */
	const [isOnboardingBannerDataLoaded, setIsOnboardingBannerDataLoaded] = useState<boolean>(false);

	const isLivePage = useIsCurrentPageLive();

	const isInCompanyHub = isCompanyHubSpaceKey(spaceKey);

	useEffect(() => {
		// Since we don't render the OnboardingEditorBanner on live pages,
		// there is no other code to set isOnboardingBannerDataLoaded to true, so we must update it here.
		// This makes sure that the editor HelpMenu shows on live pages.
		if (isLivePage && !isOnboardingBannerDataLoaded) {
			setIsOnboardingBannerDataLoaded(true);
		}
	}, [isLivePage, isOnboardingBannerDataLoaded]);

	const openHelpDialog = () => {
		editorApi?.helpDialog?.actions.openHelp();
	};

	return (
		<>
			{isOnboardingBannerDataLoaded && (
				<HelpManagerLoader
					isNCSEditor
					contentId={contentId}
					openHelpDialog={openHelpDialog}
					isContentCreationPanelOpen={isContentCreationPanelOpen}
					isCompanyHub={isCompanyHubSpaceKey(spaceKey)}
					// TODO: Add isTemplateEditor check (part of refactor of FullPageEditorWrapperComponent)
				/>
			)}
			{/* In the Company Hub editor we already show a similar banner when a user first enters
			the editor and hasn't published the hub yet,
			therefore there is no need for the OnboardingEditorBanner */}
			{!isLivePage && !isInCompanyHub && (
				<OnboardingEditorBanner
					contentId={contentId}
					isContentCreationPanelOpen={isContentCreationPanelOpen}
					isShowingOfflineBanner={isShowingOfflineBanner}
					version={version}
					spaceKey={spaceKey}
					editorActions={editorActions}
					setIsOnboardingBannerDataLoaded={setIsOnboardingBannerDataLoaded}
					onClickOpenContentCreationPanel={onClickOpenContentCreationPanel}
					handleTitleChange={handleTitleChange}
				/>
			)}
		</>
	);
};
